import React, { useState } from 'react';
import LocationTree from './LocationTree';
import DataTable from './DataTable';
import Modal from './Modal';
import './WasherManagement.css';

// 洗浄機列設定のデフォルト定義
const defaultWasherColumns = [
  { id: 'washer_name', label: '洗浄機名', visible: true, sortable: true, width: 180 },
  { id: 'washer_code', label: '洗浄機コード', visible: true, sortable: true, width: 150 },
  { id: 'serial_number', label: 'シリアル番号', visible: true, sortable: true, width: 150 },
  { id: 'model_name', label: 'モデル名', visible: true, sortable: true, width: 150 },
  { id: 'current_location_path', label: '設置先', visible: true, sortable: true, width: 250 },
  { id: 'status', label: 'ステータス', visible: true, sortable: true, width: 120 },
  { id: 'actions', label: '操作', visible: true, sortable: false, width: 380, fixed: true }
];

const WasherManagement = ({ 
  company, 
  washers, 
  locationNodes,
  locationTypes,
  onAdd, 
  onEdit, 
  onDelete,
  onAssign,
  onUnassign,
  onViewHistory,
  theme = 'light'
}) => {
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [selectedWasher, setSelectedWasher] = useState(null);
  
  // 洗浄機ページネーション・ソート用のstate
  const [washerPage, setWasherPage] = useState(() => {
    const saved = localStorage.getItem('washerPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [washerPageSize, setWasherPageSize] = useState(() => {
    const saved = localStorage.getItem('washerPageSize');
    return saved ? parseInt(saved, 10) : 20;
  });
  const [washerSort, setWasherSort] = useState(() => {
    const saved = localStorage.getItem('washerSort');
    return saved ? JSON.parse(saved) : { column: 'current_location_path', order: 'asc' };
  });

  // localStorageから洗浄機列設定を読み込む
  const loadWasherColumns = () => {
    try {
      const saved = localStorage.getItem('washerColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaultById = new Map(defaultWasherColumns.map(col => [col.id, col]));
        const normalized = [];

        parsed.forEach((savedCol) => {
          const base = defaultById.get(savedCol.id);
          if (!base) return;

          normalized.push({
            ...base,
            visible: savedCol.visible ?? base.visible,
            sortable: savedCol.sortable ?? base.sortable,
            width: savedCol.width ?? base.width,
            fixed: savedCol.fixed ?? base.fixed
          });

          defaultById.delete(savedCol.id);
        });

        return [...normalized, ...Array.from(defaultById.values())];
      }
    } catch (e) {
      console.error('Failed to load washer columns from localStorage:', e);
    }
    return defaultWasherColumns;
  };

  // 洗浄機列設定のstate
  const [washerColumns, setWasherColumns] = useState(loadWasherColumns());
  
  // ページング・ソート設定をLocalStorageに保存
  React.useEffect(() => {
    localStorage.setItem('washerPage', washerPage.toString());
  }, [washerPage]);
  
  React.useEffect(() => {
    localStorage.setItem('washerPageSize', washerPageSize.toString());
  }, [washerPageSize]);
  
  React.useEffect(() => {
    localStorage.setItem('washerSort', JSON.stringify(washerSort));
  }, [washerSort]);

  // 洗浄機データのソートとページング処理
  const getWasherPageData = () => {
    // ソート処理
    let sortedWashers = [...washers];
    if (washerSort && washerSort.column) {
      sortedWashers.sort((a, b) => {
        const aVal = a[washerSort.column];
        const bVal = b[washerSort.column];
        
        // null/undefinedの処理
        if (aVal === null || aVal === undefined || aVal === '') return 1;
        if (bVal === null || bVal === undefined || bVal === '') return -1;
        
        let comparison = 0;
        if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal, 'ja');
        } else {
          comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }
        
        return washerSort.order === 'asc' ? comparison : -comparison;
      });
    }
    
    // ページング処理
    const startIndex = (washerPage - 1) * washerPageSize;
    const endIndex = startIndex + washerPageSize;
    return sortedWashers.slice(startIndex, endIndex);
  };

  const handleAssignClick = (washer) => {
    setSelectedWasher(washer);
    setShowLocationSelector(true);
  };

  const handleLocationSelect = (locationNode) => {
    onAssign(selectedWasher, locationNode);
    setShowLocationSelector(false);
    setSelectedWasher(null);
  };

  // 洗浄機テーブル用カスタムセルレンダラー
  const renderWasherCell = (washer, column, cellStyle) => {
    switch (column.id) {
      case 'washer_name':
        return <td key={column.id} style={cellStyle}><strong>{washer.washer_name}</strong></td>;
      case 'washer_code':
        return (
          <td key={column.id} style={cellStyle}>
            {washer.washer_code ? (
              <code>{washer.washer_code}</code>
            ) : (
              <span style={{ color: '#a0aec0' }}>-</span>
            )}
          </td>
        );
      case 'serial_number':
        return <td key={column.id} style={cellStyle}>{washer.serial_number || '-'}</td>;
      case 'model_name':
        return <td key={column.id} style={cellStyle}>{washer.model_name || '-'}</td>;
      case 'current_location_path':
        return (
          <td key={column.id} style={cellStyle}>
            {washer.current_location_path ? (
              <div className="location-path">
                <span className="location-icon">📍</span>
                {washer.current_location_path}
              </div>
            ) : (
              <span className="badge badge-warning">未設置</span>
            )}
          </td>
        );
      case 'status':
        return (
          <td key={column.id} style={cellStyle}>
            {washer.status === 'active' ? (
              <span className="badge badge-success">稼働中</span>
            ) : (
              <span className="badge badge-default">{washer.status}</span>
            )}
          </td>
        );
      case 'actions':
        return (
          <td key={column.id} style={cellStyle}>
            <div className="action-buttons">
              {washer.current_assignment_id ? (
                <button
                  className="btn-small btn-warning"
                  onClick={() => onUnassign(washer)}
                  title="設置を解除"
                >
                  設置解除
                </button>
              ) : (
                <button
                  className="btn-small btn-primary"
                  onClick={() => handleAssignClick(washer)}
                  title="設置先に設置"
                >
                  設置
                </button>
              )}
              <button
                className="btn-small btn-secondary"
                onClick={() => onViewHistory(washer)}
                title="設置履歴を表示"
              >
                履歴
              </button>
              <button
                className="btn-small btn-secondary"
                onClick={() => onEdit(washer)}
              >
                編集
              </button>
              <button
                className="btn-small btn-danger"
                onClick={() => onDelete(washer)}
              >
                削除
              </button>
            </div>
          </td>
        );
      default:
        return undefined;
    }
  };

  return (
    <div className="washer-management">
      <div className="washer-list">
        <DataTable
          columns={washerColumns}
          data={getWasherPageData()}
          defaultColumns={defaultWasherColumns}
          onColumnsChange={setWasherColumns}
          localStorageKey="washerColumns"
          sortConfig={washerSort}
          onSortChange={setWasherSort}
          currentPage={washerPage}
          pageSize={washerPageSize}
          totalItems={washers.length}
          onPageChange={setWasherPage}
          onPageSizeChange={setWasherPageSize}
          idField="washer_id"
          renderCell={renderWasherCell}
          emptyMessage="洗浄機が登録されていません"
        />
      </div>

      {/* 設置先選択モーダル */}
      {showLocationSelector && (
        <Modal
          title="設置先を選択"
          onClose={() => setShowLocationSelector(false)}
          width="700px"
        >
              <div className="washer-info">
                <strong>{selectedWasher?.washer_name}</strong>を設置する設置先を選択してください。
                {selectedWasher?.current_location_path && (
                  <div className="washer-current-location">
                    現在の設置先: {selectedWasher.current_location_path}
                  </div>
                )}
              </div>
              <div className="location-selector-notice">
                ℹ️ 任意の設置先階層を選択できます
              </div>
              <LocationTree
                company={company}
                locationNodes={locationNodes}
                locationTypes={locationTypes}
                onLocationClick={handleLocationSelect}
                selectionMode={true}
                highlightAttachable={true}
              />
        </Modal>
      )}
    </div>
  );
};

export default WasherManagement;
